import type {EmptyStateProps} from '@cohort/merchants/components/EmptyState';
import EmptyState from '@cohort/merchants/components/EmptyState';
import LoadingState from '@cohort/merchants/components/LoadingState';
import PaginationFooter from '@cohort/merchants/components/PaginationFooter';
import type {PaginationDto} from '@cohort/shared/schema/common/pagination';
import {cn} from '@cohort/shared-frontend/utils/classNames';
import {ArrowsDownUp, SortDescending} from '@phosphor-icons/react';
import {SortAscending} from '@phosphor-icons/react/dist/ssr';
import {Fragment, useCallback} from 'react';
import {match, P} from 'ts-pattern';

export type TableEmptyState = EmptyStateProps & {
  isEmpty: boolean;
};

type TableHeader = {
  label: string;
  sortingKey?: string;
  width?: string;
};

type TableProps = {
  headers: Array<TableHeader>;
  children: JSX.Element[] | JSX.Element;
  sortBy?: string;
  onSort?: (sortingKey: string) => void;
  outlined?: boolean;
  className?: string;
  theadClassName?: string;
  isLoading?: boolean;
  pagination?: PaginationDto & {onPageChange?: (page: number) => void};
  emptyState?: TableEmptyState;
};

const TableHeaderCellIcon = ({
  sortBy,
  sortingKey,
}: {
  sortBy?: string;
  sortingKey?: string;
}): JSX.Element => {
  if (sortBy === sortingKey) {
    return <SortAscending className="text-muted-foreground" size={20} />;
  }
  if (sortBy === `-${sortingKey}`) {
    return <SortDescending className="text-muted-foreground" size={20} />;
  }
  if (sortingKey !== undefined) {
    return <ArrowsDownUp className="text-muted-foreground" size={20} />;
  }
  return <Fragment />;
};

const Table: React.FC<TableProps> = ({
  headers,
  children,
  sortBy,
  onSort,
  outlined,
  className,
  theadClassName,
  isLoading,
  emptyState,
  pagination,
}) => {
  const handleClick = useCallback(
    (sortingKey: string | undefined) => {
      if (onSort === undefined || sortingKey === undefined) {
        return;
      }
      if (sortBy === `-${sortingKey}`) {
        onSort(sortingKey);
      } else {
        onSort(`-${sortingKey}`);
      }
    },
    [onSort, sortBy]
  );

  const emptyGuard = (option: TableEmptyState | undefined): option is TableEmptyState =>
    option != undefined && option.isEmpty;

  const content = match({isLoading, emptyState})
    .with({isLoading: true}, () => <LoadingState />)
    .with(
      {
        emptyState: P.select(P.when(emptyGuard)),
      },
      emptyState => (
        <EmptyState
          title={emptyState.title}
          description={emptyState.description}
          cta={emptyState.cta}
        />
      )
    )
    .otherwise(() => (
      <div className="flex flex-col space-y-4">
        <table
          className={cn(
            'w-full text-sm',
            outlined
              ? 'divide-y-2 divide-slate-100 rounded-lg ring-2 ring-slate-100'
              : 'border-b-2 border-slate-100',
            className
          )}
        >
          <thead
            className={cn(
              'border-b-2 border-slate-100 bg-white text-left text-sm tracking-wide text-gray-700',
              theadClassName
            )}
          >
            <tr>
              {headers.map(header => (
                <th
                  scope="col"
                  key={header.label}
                  className={cn('py-4 pr-4 font-medium ', outlined && 'pl-4')}
                  style={{
                    width: header.width,
                  }}
                >
                  <div
                    className={cn(
                      'flex',
                      header.sortingKey !== undefined ? 'hover:cursor-pointer' : 'h-full'
                    )}
                    onClick={() => handleClick(header.sortingKey)}
                  >
                    {header.label}
                    {header.sortingKey !== undefined && (
                      <div className="my-auto ml-2">
                        <TableHeaderCellIcon sortBy={sortBy} sortingKey={header.sortingKey} />
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y-2 divide-slate-100">{children}</tbody>
        </table>
        {pagination && pagination.pages > 1 && (
          <PaginationFooter pagination={pagination} onPageChange={pagination.onPageChange} />
        )}
      </div>
    ));

  return content;
};

export default Table;
